import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { tasksAPI } from '../lib/api';

/**
 * Global indicator that shows whenever at least one task timer is running anywhere in the org.
 * Polls /api/tasks/live every 30s. Click → navigates to the Tasks page.
 */
const fmtElapsed = (startedAt) => {
  if (!startedAt) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(diffSec / 3600);
  const m = Math.floor((diffSec % 3600) / 60);
  const s = diffSec % 60;
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  if (m > 0) return `${m}m${String(s).padStart(2, '0')}`;
  return `${s}s`;
};

const LiveTimerBadge = () => {
  const [running, setRunning] = useState([]);
  const [tick, setTick] = useState(0);

  // Poll the live endpoint every 30s
  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const r = await tasksAPI.live();
        if (!cancelled) setRunning(Array.isArray(r.data) ? r.data : []);
      } catch (_e) { /* silent */ }
    };
    fetchLive();
    const iv = setInterval(fetchLive, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Re-render every second so the elapsed time updates live
  useEffect(() => {
    if (running.length === 0) return undefined;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [running.length]);

  if (running.length === 0) return null;

  const first = running[0];
  const elapsed = fmtElapsed(first.time_started_at);
  const tooltip = running.length === 1
    ? `${first.title} — ${elapsed}`
    : `${running.length} chronomètres en cours`;

  return (
    <Link
      to="/tasks"
      title={tooltip}
      aria-label={tooltip}
      data-testid="live-timer-badge"
      data-tick={tick}
      className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 transition-colors"
    >
      <span className="relative flex items-center justify-center w-2.5 h-2.5">
        <span className="absolute inset-0 rounded-full bg-amber-500 animate-ping opacity-60" />
        <span className="relative w-2 h-2 rounded-full bg-amber-500" />
      </span>
      <Clock className="w-3.5 h-3.5" strokeWidth={2} />
      <span className="text-xs font-semibold tabular-nums">
        {running.length > 1 ? `${running.length}× ${elapsed}` : elapsed}
      </span>
    </Link>
  );
};

export default LiveTimerBadge;
